import React from 'react';
import { useInView } from '../hooks/useInView';
import { useLanguage } from '../context/LanguageContext';
import { Scissors } from 'lucide-react';

const ServiceCard = ({ service, index, isInView }) => {
  return (
    <div
      className="group relative bg-[#1a1a1a] p-6 lg:p-8 hover:bg-[#242424] border border-gray-800 hover:border-[#0067ff]"
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0) rotateY(0deg)' : 'translateY(50px) rotateY(-20deg)',
        transition: `all 1.2s cubic-bezier(0.175, 0.885, 0.32, 1.275) ${0.3 + index * 0.15}s`
      }}
    >
      <div className="absolute top-0 left-0 w-0 h-0 border-t-2 border-l-2 border-transparent group-hover:border-[#0067ff] group-hover:w-8 group-hover:h-8 transition-all duration-500" />
      <div className="absolute bottom-0 right-0 w-0 h-0 border-b-2 border-r-2 border-transparent group-hover:border-[#0067ff] group-hover:w-8 group-hover:h-8 transition-all duration-500" />
      
      <div className="text-[#0067ff] mb-4 transition-all duration-500 group-hover:scale-125 group-hover:rotate-12">
        <Scissors className="w-8 h-8" />
      </div>

      <h3 className="text-white text-lg font-serif font-bold mb-2 group-hover:text-[#0067ff] transition-colors duration-300">
        {service.name}
      </h3>

      <div className="w-10 h-0.5 bg-[#0067ff] mb-3 transition-all duration-500 group-hover:w-full" />

      <p className="text-gray-400 text-sm leading-relaxed mb-3 group-hover:text-gray-300 transition-colors duration-300">
        {service.description}
      </p>

      <div className="text-[#0067ff] font-medium text-base group-hover:scale-110 transition-transform duration-300 inline-block">
        {service.price}
      </div>
    </div>
  );
};

const ServicesSection = () => {
  const { t } = useLanguage();
  const [ref, isInView] = useInView({ threshold: 0.1 });

  return (
    <section
      id="usluge"
      ref={ref}
      className="py-24 lg:py-32 bg-black"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span 
            className="text-[#0067ff] text-sm uppercase tracking-widest font-medium mb-4 block"
            style={{
              opacity: isInView ? 1 : 0,
              transform: isInView ? 'translateX(0)' : 'translateX(120px)',
              transition: 'all 1.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
            }}
          >
            {t.services.label}
          </span>
          <h2 
            className="text-4xl md:text-5xl font-serif font-bold text-white mb-6"
            style={{
              opacity: isInView ? 1 : 0,
              transform: isInView ? 'scale(1) rotate(0deg)' : 'scale(0.3) rotate(-15deg)',
              transition: 'all 1.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.3s'
            }}
          >
            {t.services.title}
          </h2>
          {t.services.subtitle && (
            <h3 
              className="text-gray-400 text-lg font-light mb-6 max-w-2xl mx-auto"
              style={{
                opacity: isInView ? 1 : 0,
                transition: 'all 1s ease-out 0.5s'
              }}
            >
              {t.services.subtitle}
            </h3>
          )}
          <div 
            className="h-1 bg-[#0067ff] mx-auto"
            style={{
              width: isInView ? '5rem' : '0',
              opacity: isInView ? 1 : 0,
              transition: 'all 1.2s ease-out 0.6s'
            }}
          />
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {t.services.items.map((service, index) => (
            <ServiceCard
              key={index}
              service={service}
              index={index}
              isInView={isInView}
            />
          ))}  
        </div>

        {/* Additional Info */}
        <div 
          className="text-center mt-12"
          style={{
            opacity: isInView ? 1 : 0,
            transition: 'opacity 1s ease-out 1.5s'
          }}
        >
          <p className="text-gray-500 text-sm">
            {t.services.info}
          </p>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
